// ** react imports
import React, { useEffect } from 'react';

// ** router imports
import { useNavigate } from 'react-router-dom';

// ** validation imports
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

// ** custom hooks imports
import { useRegisterUserByAdmin } from '../../../hooks/apis/useUsers';

// ** components imports
import NormalInput from '../../../components/common/Inputs/NormalInput';
import NormalButton from '../../../components/common/Buttons/NormalButton';
import LoadingButton from '../../../components/common/Buttons/LoadingButton';

// ** enums imports
import { BUTTON_THEMES } from '../../../enums/common';

// ** helper functions
const formatPhoneNumber = phoneValue => {
  if (!phoneValue) return '';
  // If it's already formatted, return as is
  if (
    typeof phoneValue === 'string' &&
    phoneValue.replace(/\D/g, '').length === 10
  ) {
    const cleaned = phoneValue.replace(/\D/g, '');
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
      6
    )}`;
  }
  return phoneValue;
};

function AccountPanel(props) {
  // props
  const {} = props;

  // hooks
  const navigate = useNavigate();

  // state
  const [phoneValue, setPhoneValue] = React.useState('');

  // validation schema
  const validationSchema = yup.object().shape({
    fullName: yup
      .string()
      .required('Full name required.')
      .min(3, 'Minimum 3 latter required.'),
    email: yup.string().email('Invalid email').required('Email is required'),
    phone: yup
      .string()
      .required('Phone number is required')
      .matches(
        /^[0-9\-\+\s\(\)]+$/,
        'Phone number must contain only numbers, spaces, parentheses, hyphens, and plus signs'
      )
      .min(10, 'Phone number must be at least 10 digits')
      .max(15, 'Phone number must be at most 15 characters'),
  });

  // react hook form
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onTouched',
  });

  // watch phone field to update local state
  const watchedPhone = watch('phone');

  // effect to update local state when form phone changes
  useEffect(() => {
    const rawPhone = watchedPhone ? watchedPhone.replace(/\D/g, '') : '';
    const formattedPhone = formatPhoneNumber(rawPhone);
    if (formattedPhone !== phoneValue) {
      setPhoneValue(formattedPhone);
    }
  }, [watchedPhone, phoneValue]);

  // custom hooks
  const { isRegisterUserByAdminLoading, registerUserByAdmin } =
    useRegisterUserByAdmin();

  // handlers
  const onSubmit = async data => {
    // Ensure phone number is sent as unformatted digits to match backend validation
    const submitData = {
      ...data,
      phone: data.phone.replace(/\D/g, ''), // Remove all non-digit characters
    };
    const response = await registerUserByAdmin(submitData);
    if (response.data.success) {
      navigate('/users/list');
    }
  };

  const handleCancel = () => {
    navigate('/users/list');
  };

  return (
    <div className="grow">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="p-6 space-y-6">
          <section>
            <h2 className="text-xl leading-snug text-gray-800 dark:text-gray-100 font-bold mb-1">
              Create new manager
            </h2>
            <div className="w-full md:max-w-1/2 text-sm">
              Creating a new manager role typically involves defining
              responsibilities, setting expectations, and outlining the scope of
              authority.
            </div>
            <div className="sm:flex sm:flex-wrap items-center space-y-4 sm:space-y-0 sm:space-x-4 mt-5 gap-y-4">
              <div className="sm:w-[24%]">
                <NormalInput
                  label="Full name"
                  type="text"
                  name="fullName"
                  register={register}
                  errors={errors.fullName?.message}
                />
              </div>
              <div className="sm:w-[24%]">
                <NormalInput
                  label="Email address"
                  type="email"
                  name="email"
                  register={register}
                  errors={errors.email?.message}
                />
              </div>
              <div className="sm:w-[24%]">
                <NormalInput
                  label="Phone number"
                  type="tel"
                  name="phone"
                  value={phoneValue}
                  onChange={e => {
                    const rawValue = e.target.value.replace(/\D/g, ''); // Remove formatting to get raw digits
                    const formatted = formatPhoneNumber(rawValue);
                    setPhoneValue(formatted);
                    setValue('phone', rawValue, { shouldValidate: true }); // Store unformatted for validation
                  }}
                  register={register}
                  errors={errors.phone?.message}
                />
              </div>
            </div>
          </section>
        </div>
        {/* Panel footer */}
        <footer>
          <div className="flex flex-col px-6 py-5 border-t border-gray-200 dark:border-gray-700/60">
            <div className="flex self-end gap-3">
              <NormalButton
                theme={BUTTON_THEMES.DANGER}
                onClick={() => handleCancel()}
              >
                Cancel
              </NormalButton>
              <LoadingButton
                type="submit"
                theme={BUTTON_THEMES.PRIMARY}
                isLoading={isRegisterUserByAdminLoading}
              >
                Save Changes
              </LoadingButton>
            </div>
          </div>
        </footer>
      </form>
    </div>
  );
}

export default AccountPanel;
